import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import SnackbarContent from '@mui/material/SnackbarContent';
import Box from '@mui/material/Box';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { tokens } from '../tokens';
import { Icon } from './Icon';

interface ToastOptions {
  tone?: 'ok' | 'err' | 'info';
  action?: { label: string; onClick: () => void };
}
type ShowToast = (message: string, options?: ToastOptions) => void;

const ToastContext = createContext<ShowToast | null>(null);

const ICON = {
  ok: ['check_circle', tokens.inverse.ok],
  err: ['error', '#FFB4AB'],
  info: ['info', tokens.inverse.action],
} as const;

/** Design snackbar: bottom-start, dark inverse surface, status icon, optional action and close; auto-hides after 3.8 s. */
export function ToastProvider({ children, closeLabel }: { children: ReactNode; closeLabel: string }) {
  const [state, setState] = useState<{ key: number; message: string; options: ToastOptions } | null>(null);
  const show = useCallback<ShowToast>(
    (message, options = {}) => setState({ key: Date.now(), message, options }),
    [],
  );
  const close = useCallback(() => setState(null), []);
  const value = useMemo(() => show, [show]);
  const [icon, color] = ICON[state?.options.tone ?? 'ok'];

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Snackbar
        key={state?.key}
        open={state !== null}
        autoHideDuration={3800}
        onClose={(_, reason) => reason !== 'clickaway' && close()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        sx={{ bottom: { xs: 24, sm: 24 } }}
      >
        <SnackbarContent
          role="status"
          sx={{
            maxWidth: 480,
            animation: 'fadeUp .2s ease-out',
            '& .MuiSnackbarContent-message': { py: 1.5, display: 'flex', alignItems: 'center', gap: 1.75 },
          }}
          message={
            <>
              <Icon name={icon} sx={{ color }} />
              <Box component="span">{state?.message}</Box>
            </>
          }
          action={
            <>
              {state?.options.action && (
                <Box
                  component="button"
                  onClick={() => {
                    state.options.action?.onClick();
                    close();
                  }}
                  sx={{
                    border: 0,
                    bgcolor: 'transparent',
                    color: tokens.inverse.action,
                    fontWeight: 600,
                    px: 1,
                    cursor: 'pointer',
                  }}
                >
                  {state.options.action.label}
                </Box>
              )}
              <IconButton
                size="small"
                aria-label={closeLabel}
                onClick={close}
                sx={{ color: tokens.inverse.muted }}
              >
                <Icon name="close" />
              </IconButton>
            </>
          }
        />
      </Snackbar>
    </ToastContext.Provider>
  );
}

export function useToast(): ShowToast {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
